import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useTheme } from '../contexts/AppContexts';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  ArrowLeft, Plus, Pencil, Trash2, Printer, Search,
  ChevronDown, AlertCircle, Clock, CheckCircle, XCircle, Ban
} from 'lucide-react';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const PRIORITY_CONFIG = {
  Low: { color: '#22C55E', bg: 'bg-green-500/15', text: 'text-green-400', border: 'border-green-500/30' },
  Medium: { color: '#F97316', bg: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/30' },
  High: { color: '#EF4444', bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30' },
};

const STATUS_CONFIG = {
  Open: { icon: AlertCircle, color: 'text-blue-400', bg: 'bg-blue-500/15', border: 'border-blue-500/30' },
  'In Progress': { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/30' },
  Resolved: { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/15', border: 'border-green-500/30' },
  Closed: { icon: XCircle, color: 'text-gray-400', bg: 'bg-gray-500/15', border: 'border-gray-500/30' },
  Cancelled: { icon: Ban, color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/30' },
};

const HelpDeskView = ({ currentUser }) => {
  const { theme } = useTheme();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | form | detail
  const [editingTicket, setEditingTicket] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');
  const printRef = useRef(null);

  const isSuperAdmin = currentUser?.role === 'Super Admin';
  const isOwner = (ticket) => ticket?.submitted_by_id === currentUser?.id;
  const canManage = (ticket) => isSuperAdmin || isOwner(ticket);
  const token = secureStorage.getToken();
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/tickets`, { headers });
      setTickets(res.data);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, []);

  const handleDelete = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket?')) return;
    try {
      await axios.delete(`${API}/tickets/${ticketId}`, { headers });
      setTickets(prev => prev.filter(t => t.id !== ticketId));
      if (selectedTicket?.id === ticketId) { setView('list'); setSelectedTicket(null); }
    } catch (err) {
      alert('Failed to delete ticket');
    }
  };

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open('', '_blank');
    if (!win) return;
    let printHTML = `
      <html><head><title>Ticket Report</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 24px; color: #1a1a1a; }
        h1 { font-size: 22px; margin-bottom: 4px; }
        h2 { font-size: 16px; color: #555; margin-bottom: 16px; font-weight: normal; }
        .field { margin-bottom: 12px; }
        .label { font-weight: bold; font-size: 13px; color: #333; margin-bottom: 2px; }
        .value { font-size: 14px; color: #444; white-space: pre-wrap; }
        .meta { display: flex; gap: 24px; margin-bottom: 16px; flex-wrap: wrap; }
        .meta-item { font-size: 13px; }
        .meta-item strong { color: #333; }
        hr { border: none; border-top: 1px solid #ddd; margin: 16px 0; }
        table { width: 100%; border-collapse: collapse; margin-top: 16px; }
        th, td { border: 1px solid #ddd; padding: 8px 10px; text-align: left; font-size: 13px; }
        th { background: #f5f5f5; font-weight: bold; }
        @media print { body { padding: 0; } }
      </style></head><body>
    `;
    if (selectedTicket) {
      const t = selectedTicket;
      printHTML += `
        <h1>${t.subject}</h1>
        <h2>Ticket ID: ${t.id}</h2>
        <div class="meta">
          <div class="meta-item"><strong>Priority:</strong> ${t.priority}</div>
          <div class="meta-item"><strong>Status:</strong> ${t.status}</div>
          <div class="meta-item"><strong>Submitted By:</strong> ${t.submitted_by}</div>
          <div class="meta-item"><strong>Created:</strong> ${new Date(t.created_at).toLocaleString()}</div>
          <div class="meta-item"><strong>Modified:</strong> ${new Date(t.modified_at).toLocaleString()}</div>
        </div>
        <hr/>
        <div class="field"><div class="label">Description</div><div class="value">${t.description || 'N/A'}</div></div>
        <div class="field"><div class="label">Recommendation</div><div class="value">${t.recommendation || 'N/A'}</div></div>
        <div class="field"><div class="label">Response</div><div class="value">${t.response || 'N/A'}</div></div>
      `;
    } else {
      printHTML += `<h1>Help Desk Tickets Report</h1><h2>Generated: ${new Date().toLocaleString()}</h2>`;
      printHTML += `<table><thead><tr><th>Subject</th><th>Priority</th><th>Status</th><th>Submitted By</th><th>Created</th></tr></thead><tbody>`;
      filteredTickets.forEach(t => {
        printHTML += `<tr><td>${t.subject}</td><td>${t.priority}</td><td>${t.status}</td><td>${t.submitted_by}</td><td>${new Date(t.created_at).toLocaleString()}</td></tr>`;
      });
      printHTML += `</tbody></table>`;
    }
    printHTML += `</body></html>`;
    writeSanitizedHTML(win.document, printHTML);
    win.focus();
    win.print();
  };

  const filteredTickets = tickets.filter(t => {
    const matchSearch = !searchQuery || 
      t.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.submitted_by?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = filterStatus === 'All' || t.status === filterStatus;
    const matchPriority = filterPriority === 'All' || t.priority === filterPriority;
    return matchSearch && matchStatus && matchPriority;
  });

  // ─── Form View ───
  if (view === 'form') {
    return (
      <TicketForm
        theme={theme}
        editingTicket={editingTicket}
        isSuperAdmin={isSuperAdmin}
        headers={headers}
        onBack={() => { setView('list'); setEditingTicket(null); }}
        onSave={() => { fetchTickets(); setView('list'); setEditingTicket(null); }}
      />
    );
  }

  // ─── Detail View ───
  if (view === 'detail' && selectedTicket) {
    const t = selectedTicket;
    const pCfg = PRIORITY_CONFIG[t.priority] || PRIORITY_CONFIG.Low;
    const sCfg = STATUS_CONFIG[t.status] || STATUS_CONFIG.Open;
    const StatusIcon = sCfg.icon;
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-6" ref={printRef}>
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => { setView('list'); setSelectedTicket(null); }}
            className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-black"
            data-testid="ticket-detail-back">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Tickets
          </Button>
          <div className="flex gap-2">
            {canManage(t) && (
              <>
                <Button variant="outline" onClick={handlePrint}
                  className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-black"
                  data-testid="ticket-print-single">
                  <Printer className="h-4 w-4 mr-2" /> Print
                </Button>
                <Button variant="outline" onClick={() => { setEditingTicket(t); setView('form'); }}
                  className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-black"
                  data-testid="ticket-edit-btn">
                  <Pencil className="h-4 w-4 mr-2" /> Edit
                </Button>
                <Button variant="outline" onClick={() => handleDelete(t.id)}
                  className="text-red-400 border-red-500 hover:bg-red-500 hover:text-white"
                  data-testid="ticket-delete-btn">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </Button>
              </>
            )}
          </div>
        </div>

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <div className="flex items-start justify-between">
              <CardTitle className="text-2xl text-white">{t.subject}</CardTitle>
              <div className="flex gap-2">
                <Badge className={`${pCfg.bg} ${pCfg.text} ${pCfg.border} border`}>{t.priority}</Badge>
                <Badge className={`${sCfg.bg} ${sCfg.color} ${sCfg.border} border flex items-center gap-1`}>
                  <StatusIcon className="h-3 w-3" /> {t.status}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><span className="text-gray-500">Submitted By</span><p className="text-white">{t.submitted_by}</p></div>
              <div><span className="text-gray-500">Created</span><p className="text-white">{new Date(t.created_at).toLocaleString()}</p></div>
              <div><span className="text-gray-500">Modified</span><p className="text-white">{new Date(t.modified_at).toLocaleString()}</p></div>
              <div><span className="text-gray-500">Ticket ID</span><p className="text-white text-xs font-mono">{t.id?.slice(0, 8)}...</p></div>
            </div>

            <hr className="border-gray-700" />

            <div>
              <Label className="text-gray-400 text-sm">Description</Label>
              <p className="text-white mt-1 whitespace-pre-wrap">{t.description || 'N/A'}</p>
            </div>

            <div>
              <Label className="text-gray-400 text-sm">Recommendation</Label>
              <p className="text-white mt-1 whitespace-pre-wrap">{t.recommendation || 'N/A'}</p>
            </div>

            <div>
              <Label className="text-gray-400 text-sm">Response</Label>
              <p className="text-white mt-1 whitespace-pre-wrap">{t.response || 'No response yet'}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── List View ───
  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" ref={printRef}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-orange-500">Help Desk</h1>
          <p className="text-gray-400 text-sm mt-1">{filteredTickets.length} ticket{filteredTickets.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          {isSuperAdmin && (
            <Button variant="outline" onClick={handlePrint}
              className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-black"
              data-testid="ticket-print-all">
              <Printer className="h-4 w-4 mr-2" /> Print Report
            </Button>
          )}
          <Button onClick={() => { setEditingTicket(null); setView('form'); }}
            className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
            data-testid="ticket-new-btn">
            <Plus className="h-4 w-4 mr-2" /> New Ticket
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Search tickets..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10 bg-gray-800 border-gray-700 text-white"
            data-testid="ticket-search"
          />
        </div>
        <div className="relative">
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="appearance-none bg-gray-800 border border-gray-700 text-white rounded-md px-4 py-2 pr-8 text-sm"
            data-testid="ticket-filter-status">
            <option value="All">All Status</option>
            {Object.keys(STATUS_CONFIG).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
        </div>
        <div className="relative">
          <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
            className="appearance-none bg-gray-800 border border-gray-700 text-white rounded-md px-4 py-2 pr-8 text-sm"
            data-testid="ticket-filter-priority">
            <option value="All">All Priority</option>
            {Object.keys(PRIORITY_CONFIG).map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
        </div>
      </div>

      {/* Ticket List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading tickets...</div>
      ) : filteredTickets.length === 0 ? (
        <div className="text-center py-16">
          <AlertCircle className="h-12 w-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-lg">No tickets found</p>
          <p className="text-gray-600 text-sm mt-1">Click "New Ticket" to submit a help request</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTickets.map(ticket => {
            const pCfg = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.Low;
            const sCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.Open;
            const StatusIcon = sCfg.icon;
            return (
              <Card key={ticket.id}
                className="bg-gray-800 border-gray-700 hover:border-orange-500/40 transition-colors cursor-pointer"
                onClick={() => { setSelectedTicket(ticket); setView('detail'); }}
                data-testid={`ticket-card-${ticket.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 mr-4">
                      <h3 className="text-white font-semibold truncate">{ticket.subject}</h3>
                      <p className="text-gray-500 text-sm mt-1 truncate">{ticket.description}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span>{ticket.submitted_by}</span>
                        <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={`${pCfg.bg} ${pCfg.text} ${pCfg.border} border text-xs`}>{ticket.priority}</Badge>
                      <Badge className={`${sCfg.bg} ${sCfg.color} ${sCfg.border} border text-xs flex items-center gap-1`}>
                        <StatusIcon className="h-3 w-3" /> {ticket.status}
                      </Badge>
                      {canManage(ticket) && (
                        <div className="flex gap-1 ml-2" onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" onClick={() => { setEditingTicket(ticket); setView('form'); }}
                            className="text-gray-400 hover:text-orange-500 p-1 h-auto">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(ticket.id)}
                            className="text-gray-400 hover:text-red-500 p-1 h-auto">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Ticket Form Component ───
const TicketForm = ({ theme, editingTicket, isSuperAdmin, headers, onBack, onSave }) => {
  const [formData, setFormData] = useState({
    subject: '', description: '', recommendation: '', response: '',
    priority: 'Low', status: 'Open',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingTicket) {
      setFormData({
        subject: editingTicket.subject || '',
        description: editingTicket.description || '',
        recommendation: editingTicket.recommendation || '',
        response: editingTicket.response || '',
        priority: editingTicket.priority || 'Low',
        status: editingTicket.status || 'Open',
      });
    }
  }, [editingTicket]);

  const validate = () => {
    const errs = {};
    if (!formData.subject.trim()) errs.subject = 'Subject is required';
    if (!formData.description.trim()) errs.description = 'Description is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      if (editingTicket) {
        await axios.put(`${API}/tickets/${editingTicket.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/tickets`, {
          subject: formData.subject,
          description: formData.description,
          recommendation: formData.recommendation,
          priority: formData.priority,
        }, { headers });
      }
      onSave();
    } catch (err) {
      alert('Failed to save ticket: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-orange-500">
          {editingTicket ? 'Edit Ticket' : 'Submit New Ticket'}
        </h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onBack}
            className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-black">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <Button type="submit" form="ticket-form" disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 text-black font-semibold px-6"
            data-testid="ticket-form-submit">
            {saving ? 'Saving...' : (editingTicket ? 'Update Ticket' : 'Submit Ticket')}
          </Button>
        </div>
      </div>

      <form id="ticket-form" onSubmit={handleSubmit} className="space-y-5">
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-5 space-y-4">
            <div>
              <Label className="text-white">Subject <span className="text-red-500">*</span></Label>
              <Input value={formData.subject}
                onChange={e => { setFormData(p => ({ ...p, subject: e.target.value })); setErrors(p => ({ ...p, subject: '' })); }}
                placeholder="Brief summary of the issue"
                className="bg-gray-900 border-gray-600 text-white mt-1"
                data-testid="ticket-subject" />
              {errors.subject && <p className="text-red-400 text-sm mt-1">{errors.subject}</p>}
            </div>

            <div>
              <Label className="text-white">Description <span className="text-red-500">*</span></Label>
              <Textarea value={formData.description}
                onChange={e => { setFormData(p => ({ ...p, description: e.target.value })); setErrors(p => ({ ...p, description: '' })); }}
                placeholder="Detailed description of the issue..."
                rows={4} className="bg-gray-900 border-gray-600 text-white mt-1"
                data-testid="ticket-description" />
              {errors.description && <p className="text-red-400 text-sm mt-1">{errors.description}</p>}
            </div>

            <div>
              <Label className="text-white">Recommendation</Label>
              <Textarea value={formData.recommendation}
                onChange={e => setFormData(p => ({ ...p, recommendation: e.target.value }))}
                placeholder="Suggested resolution or recommendation..."
                rows={3} className="bg-gray-900 border-gray-600 text-white mt-1"
                data-testid="ticket-recommendation" />
            </div>

            <div>
              <Label className="text-white">Priority</Label>
              <div className="flex gap-3 mt-2">
                {['Low', 'Medium', 'High'].map(p => {
                  const cfg = PRIORITY_CONFIG[p];
                  const selected = formData.priority === p;
                  return (
                    <button key={p} type="button"
                      onClick={() => setFormData(prev => ({ ...prev, priority: p }))}
                      className={`px-4 py-2 rounded-md border text-sm font-medium transition-all ${
                        selected ? `${cfg.bg} ${cfg.text} ${cfg.border}` : 'bg-gray-900 text-gray-400 border-gray-700 hover:border-gray-500'
                      }`}
                      data-testid={`ticket-priority-${p.toLowerCase()}`}>
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Response and Status — Super Admin only when editing */}
            {isSuperAdmin && editingTicket && (
              <>
                <div>
                  <Label className="text-white">Response</Label>
                  <Textarea value={formData.response}
                    onChange={e => setFormData(p => ({ ...p, response: e.target.value }))}
                    placeholder="Admin response to the ticket..."
                    rows={3} className="bg-gray-900 border-gray-600 text-white mt-1"
                    data-testid="ticket-response" />
                </div>

                <div>
                  <Label className="text-white">Status</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(STATUS_CONFIG).map(([status, cfg]) => {
                      const Icon = cfg.icon;
                      const selected = formData.status === status;
                      return (
                        <button key={status} type="button"
                          onClick={() => setFormData(prev => ({ ...prev, status }))}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-all ${
                            selected ? `${cfg.bg} ${cfg.color} ${cfg.border}` : 'bg-gray-900 text-gray-400 border-gray-700 hover:border-gray-500'
                          }`}
                          data-testid={`ticket-status-${status.toLowerCase().replace(/\s/g, '-')}`}>
                          <Icon className="h-3.5 w-3.5" /> {status}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Bottom submit */}
        <div className="sticky bottom-0 bg-gray-900/95 backdrop-blur-sm border-t border-gray-700 p-4 flex justify-end gap-3 z-10">
          <Button type="button" variant="outline" onClick={onBack}
            className="border-gray-600 text-gray-400 hover:bg-gray-700">
            Cancel
          </Button>
          <Button type="submit" disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 text-black font-semibold px-8"
            data-testid="ticket-form-submit-bottom">
            {saving ? 'Saving...' : (editingTicket ? 'Update Ticket' : 'Submit Ticket')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default HelpDeskView;
